import React, { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { FeatureCollection, Point } from 'geojson'
import { Layer, MapMouseEvent, Source } from 'react-map-gl/mapbox'
import styled from 'styled-components'
import Map from 'core-system/Map/Map'
import MapNavigationControl from 'core-system/Map/MapNavigationControl'
import palette from 'core-system/Themes/palette'
import WorksiteDistributionHover from './WorksiteDistributionHover'
import {
  hoverTooltipPosition,
  worksiteHoverDetails,
  WorksiteHoverDetails,
} from './worksiteDistribution'

const LAYER_ID = 'worksitePeople'

const HoverOverlay = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 2;
  overflow: hidden;
`

const HoverCard = styled.div`
  position: absolute;
  max-width: calc(100% - 1rem);
  border-radius: 0.75rem;
  background: ${(props) => props.theme.palette.white};
  box-shadow: ${(props) => props.theme.dropShadows.normal};
  padding: 0.625rem 0.75rem;
`

type ProjectableMap = {
  project: (lngLat: [number, number]) => { x: number; y: number }
}

const WorksiteHoverTooltip = ({
  hover,
  map,
}: {
  hover: HoverState
  map: ProjectableMap | null
}) => {
  const overlayRef = useRef<HTMLDivElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const [box, setBox] = useState({ left: 8, top: 8, ready: false })

  useLayoutEffect(() => {
    const overlay = overlayRef.current
    const card = cardRef.current
    if (!overlay || !card || !map) {
      return
    }
    const point = map.project([hover.lng, hover.lat])
    const next = hoverTooltipPosition({
      x: point.x,
      y: point.y,
      width: card.offsetWidth,
      height: card.offsetHeight,
      containerWidth: overlay.clientWidth,
      containerHeight: overlay.clientHeight,
    })
    setBox({ ...next, ready: true })
  }, [hover, map])

  return (
    <HoverOverlay ref={overlayRef}>
      <HoverCard
        ref={cardRef}
        style={{
          left: box.left,
          top: box.top,
          visibility: box.ready ? 'visible' : 'hidden',
        }}
      >
        <WorksiteDistributionHover details={hover} />
      </HoverCard>
    </HoverOverlay>
  )
}

interface HoverState extends WorksiteHoverDetails {
  id: string
  lng: number
  lat: number
}

interface WorksiteDistributionMapProps {
  geojson: FeatureCollection<Point>
  onSiteClick: (locationKey: string) => void
}

const WorksiteDistributionMap = ({
  geojson,
  onSiteClick,
}: WorksiteDistributionMapProps) => {
  const [hover, setHover] = useState<HoverState | null>(null)
  const mapRef = useRef<ProjectableMap | null>(null)

  const onMouseMove = useCallback((event: MapMouseEvent) => {
    mapRef.current = event.target
    const feature = event.features?.find(
      (item) => item.layer?.id === LAYER_ID
    )
    if (!feature || feature.geometry.type !== 'Point') {
      setHover(null)
      return
    }
    const [lng, lat] = feature.geometry.coordinates
    const id = String(feature.properties?.id ?? '')
    setHover((current) => {
      if (current?.id === id) {
        return current
      }
      return {
        id,
        lng,
        lat,
        ...worksiteHoverDetails(feature.properties ?? {}),
      }
    })
  }, [])

  const onMouseLeave = useCallback(() => setHover(null), [])

  const onClick = useCallback(
    (event: MapMouseEvent) => {
      const feature = event.features?.find(
        (item) => item.layer?.id === LAYER_ID
      )
      if (!feature) {
        return
      }
      onSiteClick(String(feature.properties?.locationKey ?? ''))
    },
    [onSiteClick]
  )

  return (
    <Map
      style={{ height: '100%', width: '100%' }}
      scrollZoom={false}
      cursor={hover ? 'pointer' : 'auto'}
      interactiveLayerIds={[LAYER_ID]}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      loadLayer={geojson.features.length > 0 ? geojson : undefined}
    >
      {(mapProps) => (
        <>
          <Source id={LAYER_ID} type='geojson' data={geojson}>
            <Layer
              id={LAYER_ID}
              type='circle'
              beforeId='settlement-major-label'
              layout={{
                'circle-sort-key': ['*', -1, ['get', 'employeeCount']],
              }}
              paint={{
                'circle-radius': ['get', 'radius'],
                'circle-color': ['get', 'color'],
                'circle-opacity': 0.9,
                'circle-stroke-width': 1.5,
                'circle-stroke-color': palette.white,
              }}
            />
          </Source>
          {hover && (
            <WorksiteHoverTooltip hover={hover} map={mapRef.current} />
          )}
          <MapNavigationControl
            setViewport={mapProps.setViewport}
            featureCollection={geojson}
            updateBbox={mapProps.updateBbox}
          />
        </>
      )}
    </Map>
  )
}

export default React.memo(WorksiteDistributionMap)
